import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Zap, Crown, X, Check, ArrowRight, Loader2,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    base: 'STARTER', name: 'Starter', monthly: 79, icon: Zap,
    color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20',
    sms: 500, voice: 150,
    features: ['AI call handling', '500 SMS/mo', '150 AI minutes/mo', 'Lead capture', 'Basic analytics'],
    locked: ['Premium voices', 'Multilingual', 'Advanced automation'],
  },
  {
    base: 'PRO', name: 'Pro', monthly: 129, icon: Crown, popular: true,
    color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20',
    sms: 1000, voice: 300,
    features: ['Everything in Starter', '1,000 SMS/mo', '300 AI minutes/mo', 'Premium voices', 'Multilingual support', 'Priority support'],
    locked: ['Advanced automation'],
  },
  {
    base: 'ELITE', name: 'Elite', monthly: 199, icon: Crown,
    color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20',
    sms: 3000, voice: 1500,
    features: ['Everything in Pro', '3,000 SMS/mo', '1,500 AI minutes/mo', 'Advanced automation', 'Priority support', 'Custom integrations'],
    locked: [],
  },
];

export function UpgradeModal({ isOpen, onClose, trigger, currentTier }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState('');

  if (!isOpen) return null;

  const handleUpgrade = async (planBase) => {
    const code = `AGENT9_${planBase}_MONTHLY`;
    setLoading(code);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = currentTier ? '/api/billing/upgrade' : '/api/billing/subscribe';
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, { plan_code: code }, { headers });
      if (data.hosted_payment_url) {
        window.location.href = data.hosted_payment_url;
      } else {
        onClose();
        navigate('/settings/billing');
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      onClose();
      navigate('/settings/billing');
    } finally {
      setLoading('');
    }
  };

  const triggerMessages = {
    limit_hit: 'You\'ve reached your plan limits. Upgrade for more capacity.',
    locked_feature: 'This feature requires a higher plan.',
    analytics_upsell: 'Get deeper insights with a Pro or Elite plan.',
    default: 'Unlock more features and higher limits.',
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="upgrade-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl max-w-3xl w-full mx-4 p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground" data-testid="close-upgrade-modal">
          <X size={20} />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-foreground">Upgrade Your Plan</h2>
          <p className="text-sm text-muted-foreground mt-1">{triggerMessages[trigger] || triggerMessages.default}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {PLANS.map(p => {
            const code = `AGENT9_${p.base}_MONTHLY`;
            const isCurrent = currentTier === p.base.toLowerCase();
            const Icon = p.icon;
            return (
              <div key={p.base} className={`relative rounded-xl border p-4 transition-all ${
                isCurrent ? 'border-emerald-500/40 bg-emerald-500/5' :
                p.popular ? `${p.border} bg-card/80 ring-1 ring-amber-500/20` :
                'border-border bg-card'
              }`} data-testid={`upgrade-plan-${p.base.toLowerCase()}`}>
                {p.popular && (
                  <span className="absolute -top-2.5 left-3 text-[9px] bg-amber-500 text-black px-2 py-0.5 rounded-full font-bold">MOST POPULAR</span>
                )}
                <div className={`h-9 w-9 rounded-lg ${p.bg} flex items-center justify-center mb-3`}>
                  <Icon size={18} className={p.color} />
                </div>
                <h3 className="text-base font-semibold text-foreground">{p.name}</h3>
                <p className="text-lg font-bold text-foreground mt-1">${p.monthly}<span className="text-xs text-muted-foreground font-normal">/mo</span></p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{p.sms} SMS + {p.voice} AI min</p>

                <div className="mt-3 space-y-1.5">
                  {p.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs text-foreground">
                      <Check size={12} className="text-emerald-400 shrink-0" /> {f}
                    </div>
                  ))}
                </div>

                <div className="mt-4">
                  {isCurrent ? (
                    <Badge className="w-full justify-center bg-emerald-500/10 text-emerald-400 border-emerald-500/20 py-1.5">Current Plan</Badge>
                  ) : (
                    <Button size="sm" onClick={() => handleUpgrade(p.base)}
                      disabled={!!loading}
                      className={`w-full text-xs gap-1 ${
                        p.popular ? 'bg-amber-500 hover:bg-amber-400 text-black' :
                        'bg-accent hover:bg-muted-foreground text-foreground'
                      }`}
                      data-testid={`upgrade-btn-${p.base.toLowerCase()}`}>
                      {loading === code ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                      {currentTier ? 'Switch' : 'Subscribe'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-[10px] text-muted-foreground mt-4">
          All plans include overage billing — your AI keeps working even after limits.
        </p>
      </div>
    </div>
  );
}

export function useUpgradeModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [trigger, setTrigger] = useState('default');

  const openUpgrade = (triggerReason = 'default') => {
    setTrigger(triggerReason);
    setIsOpen(true);
  };

  const closeUpgrade = () => {
    setIsOpen(false);
    setTrigger('default');
  };

  return { isOpen, trigger, openUpgrade, closeUpgrade };
}
